import React from 'react'
import { useSelector } from 'react-redux'
import DisconnectedData from './DisconnectedData.container'
import LoadingContent from '../../components/Placeholders/LoadingContent'
import Tooltip from '../../components/Basics/Tooltip'
import Typography from '../../components/Basics/Typography'
import { Box, Paper } from '@material-ui/core'
import theme from '../../config/styles/theme'

const SCALE_MAX_HOURS = 36
// Ancho mínimo de TODA barra visible (% de la pista). Antes sólo las rojas («en zona», 0 h)
// tenían piso, así que salían más anchas que una ámbar de 0,4 h dibujada a escala y se leían
// como «más horas». Con el mismo piso para todas, el largo nunca contradice el valor.
const MIN_BAR_PCT = 3
const AMBER_THRESHOLD_HOURS = 4
const IMPRECISE_THRESHOLD_HOURS = 16

const WEEKDAYS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb']

const COLORS = {
  critical: theme.palette.brand.error[50],
  warning: theme.palette.brand.warning[50],
  normal: theme.palette.brand.gray[50],
  impreciseNormal: theme.palette.brand.gray[75],
  empty: theme.palette.brand.gray[90],
  track: theme.palette.brand.gray[95],
  axis: theme.palette.brand.gray[65]
}

const pad = num => String(num).padStart(2, '0')

// hora_estimada_carga / hora_estimada_critico come as "YYYY-MM-DD HH:MM:SS" mine-local
// wall time with no timezone suffix — parsed via components, never via `new Date(string)`,
// so the browser never reinterprets it as UTC.
const parseLocalDateTime = value => {
  if (!value) return null
  const [datePart, timePart = '00:00:00'] = value.split(' ')
  const [year, month, day] = datePart.split('-').map(Number)
  const [hour, minute] = timePart.split(':').map(Number)
  if ([year, month, day, hour, minute].some(Number.isNaN)) return null
  return { year, month, day, hour, minute }
}

const daysBetween = (from, to) => {
  const fromUTC = Date.UTC(from.year, from.month - 1, from.day)
  const toUTC = Date.UTC(to.year, to.month - 1, to.day)
  return Math.round((toUTC - fromUTC) / 86400000)
}

const todayParts = () => {
  const now = new Date()
  return { year: now.getFullYear(), month: now.getMonth() + 1, day: now.getDate() }
}

// exact "HH:MM", weekday-prefixed when the date isn't today
const formatExactTime = value => {
  const parsed = parseLocalDateTime(value)
  if (!parsed) return null
  const hhmm = `${pad(parsed.hour)}:${pad(parsed.minute)}`
  const diffDays = daysBetween(todayParts(), parsed)
  if (diffDays === 0) return hhmm
  const weekday = WEEKDAYS[new Date(parsed.year, parsed.month - 1, parsed.day).getDay()]
  return `${weekday} ${hhmm}`
}

// approximate label for imprecise (>16h) estimates — no exact minutes, per validated +8h median bias
const formatApproxTime = value => {
  const parsed = parseLocalDateTime(value)
  if (!parsed) return 'aprox. ~'
  const diffDays = daysBetween(todayParts(), parsed)
  if (diffDays === 0) return 'hoy ~'
  if (diffDays === 1) return 'mañana ~'
  const weekday = WEEKDAYS[new Date(parsed.year, parsed.month - 1, parsed.day).getDay()]
  return `${weekday} ~`
}

const formatHours = hours => hours.toFixed(1).replace('.', ',')

const getStatus = truck => {
  if (truck.enviar_a_carga) return 'critical'
  if (truck.horas_hasta_carga === null) return 'unknown'
  if (truck.horas_hasta_carga < AMBER_THRESHOLD_HOURS) return 'warning'
  if (truck.horas_hasta_carga > IMPRECISE_THRESHOLD_HOURS) return 'impreciseNormal'
  return 'normal'
}

const getSortKey = truck => {
  if (truck.enviar_a_carga) return -Infinity
  if (truck.horas_hasta_carga === null) return Infinity
  return truck.horas_hasta_carga
}

const renderValueLabel = (truck, status) => {
  if (status === 'critical') {
    return <span style={{ color: COLORS.critical, fontWeight: 700 }}>En zona</span>
  }
  if (status === 'unknown') {
    return <span style={{ color: theme.palette.brand.gray[65] }}>Sin estimación</span>
  }
  const hours = formatHours(truck.horas_hasta_carga)
  if (status === 'impreciseNormal') {
    return (
      <span style={{ color: theme.palette.brand.gray[50] }}>
        {hours} h · {formatApproxTime(truck.hora_estimada_carga)}
      </span>
    )
  }
  const time = formatExactTime(truck.hora_estimada_carga)
  return (
    <span style={{ color: theme.palette.brand.gray[20] }}>
      {status === 'warning' && <span style={{ fontWeight: 700 }}>Próximo</span>}
      {status === 'warning' && ' · '}
      {hours} h{time ? ` · ${time}` : ''}
    </span>
  )
}

const renderTooltipContent = truck => (
  <span>
    {truck.patent}
    <br />
    Combustible: {Math.round(truck.percent)}%<br />
    Horas hasta abastecimiento: {truck.horas_hasta_carga !== null ? `${formatHours(truck.horas_hasta_carga)} h` : '—'}
    <br />
    Hora estimada de abastecimiento: {truck.hora_estimada_carga || '—'}
    <br />
    Hora estimada de nivel crítico: {truck.hora_estimada_critico || '—'}
    <br />
    Enviar a carga: {truck.enviar_a_carga ? 'Sí' : 'No'}
  </span>
)

const AXIS_MARKS = [
  { hours: 0, label: '0 h' },
  { hours: AMBER_THRESHOLD_HOURS, label: '4 h' },
  { hours: IMPRECISE_THRESHOLD_HOURS, label: '16 h' },
  { hours: SCALE_MAX_HOURS, label: '36 h+' }
]

// equipo · % de combustible · barra · horas / hora estimada
const GRID_COLUMNS = '100px 56px 1fr 190px'

const axisMarkTransform = hours => {
  if (hours === 0) return 'none'
  if (hours === SCALE_MAX_HOURS) return 'translateX(-100%)'
  return 'translateX(-50%)'
}

/**
 * The FuelingForecastChart's container.
 * Fleet-wide bar chart of hours remaining until the send-to-fuel threshold, one bar
 * per truck, sorted most-urgent first. Complements the detailed FuelingForecastTable.
 */
const FuelingForecastChart = () => {
  const { error, loading, trucks } = useSelector(state => state.fuelLevel)

  const sortedTrucks = [...trucks].sort((a, b) => getSortKey(a) - getSortKey(b))

  return (
    <>
      {loading && <LoadingContent />}
      {!loading && error && <DisconnectedData />}
      {!loading && !error && (
        <Box style={{ marginLeft: '10px', minWidth: '5vw', width: '100%' }}>
          <Typography variant="h2" margin="0 0 8px 0">
            Horas hasta abastecimiento por equipo
          </Typography>
          <Typography variant="body2" color="gray" margin="0 0 20px 0">
            Ordenado del más urgente al menos urgente. Se actualiza junto con las predicciones (cada 5 a 10 minutos).
            Rojo: en zona de carga. Ámbar: menos de 4 h para el umbral. Barras atenuadas: estimación a más de 16 h,
            referencial (sesgo medio validado de +8 h a esos horizontes).
          </Typography>
          <Paper style={{ padding: '16px 20px', maxHeight: '70vh', overflow: 'auto' }}>
            {sortedTrucks.length === 0 ? (
              <Typography variant="h2" margin="0 0 20px 0">
                Sin data
              </Typography>
            ) : (
              <Box style={{ display: 'grid', gridTemplateColumns: GRID_COLUMNS, rowGap: '10px', alignItems: 'center' }}>
                <div />
                <div />
                <div style={{ position: 'relative', height: '14px' }}>
                  {AXIS_MARKS.map(mark => (
                    <span
                      key={mark.label}
                      style={{
                        position: 'absolute',
                        left: `${(mark.hours / SCALE_MAX_HOURS) * 100}%`,
                        transform: axisMarkTransform(mark.hours),
                        fontSize: '10px',
                        color: COLORS.axis
                      }}
                    >
                      {mark.label}
                    </span>
                  ))}
                </div>
                <div />

                {sortedTrucks.map(truck => {
                  const status = getStatus(truck)
                  const pct =
                    status === 'critical' || status === 'unknown'
                      ? 0
                      : (Math.min(truck.horas_hasta_carga, SCALE_MAX_HOURS) / SCALE_MAX_HOURS) * 100

                  return (
                    <React.Fragment key={truck.patent}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {truck.patent}
                      </Typography>

                      <Box
                        style={{
                          fontSize: '13px',
                          textAlign: 'right',
                          paddingRight: '12px',
                          whiteSpace: 'nowrap',
                          color: status === 'critical' ? COLORS.critical : theme.palette.brand.gray[20],
                          fontWeight: status === 'critical' ? 700 : 400
                        }}
                      >
                        {truck.percent !== null && truck.percent !== undefined ? `${Math.round(truck.percent)}%` : '—'}
                      </Box>

                      <Tooltip content={renderTooltipContent(truck)}>
                        <div
                          style={{
                            position: 'relative',
                            height: '18px',
                            borderRadius: '4px',
                            background: COLORS.track,
                            overflow: 'hidden'
                          }}
                        >
                          {status !== 'unknown' && (
                            <div
                              style={{
                                position: 'absolute',
                                left: 0,
                                top: 0,
                                bottom: 0,
                                width: `${Math.max(pct, MIN_BAR_PCT)}%`,
                                background: COLORS[status],
                                borderRadius: '0 4px 4px 0'
                              }}
                            />
                          )}
                        </div>
                      </Tooltip>

                      <Box style={{ fontSize: '14px', paddingLeft: '10px', whiteSpace: 'nowrap' }}>
                        {renderValueLabel(truck, status)}
                      </Box>
                    </React.Fragment>
                  )
                })}
              </Box>
            )}
          </Paper>
        </Box>
      )}
    </>
  )
}

export default FuelingForecastChart
